import { useMemo, useEffect, useState } from "react";

import type { AuthUser } from "@/HelloCashflowDomain/Authentication/types/auth.types";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "SimpleProtectedRoute",
);

type UseAdminClaimCheckArgs = {
    adminE2eBypassEnabled: boolean;
    requiredClaims: string[];
    user: AuthUser | null;
};

type UseAdminClaimCheckResult = {
    hasAdminClaim: boolean;
    isCheckingClaims: boolean;
    requiresAdminClaim: boolean;
};

export const useAdminClaimCheck = ({
    adminE2eBypassEnabled,
    requiredClaims,
    user,
}: UseAdminClaimCheckArgs): UseAdminClaimCheckResult => {
    const requiresAdminClaim = useMemo(
        () => requiredClaims.includes("admin"),
        [requiredClaims],
    );
    const [isCheckingClaims, setIsCheckingClaims] = useState(false);
    const [hasAdminClaim, setHasAdminClaim] = useState(false);

    useEffect(() => {
        if (adminE2eBypassEnabled) {
            setHasAdminClaim(true);
            setIsCheckingClaims(false);
            return;
        }

        if (!requiresAdminClaim) {
            setHasAdminClaim(true);
            setIsCheckingClaims(false);
            return;
        }

        if (!user) {
            setHasAdminClaim(false);
            setIsCheckingClaims(false);
            return;
        }

        let cancelled = false;
        setIsCheckingClaims(true);

        const checkAdminClaims = async () => {
            try {
                const cachedTokenResult = await user.getIdTokenResult();
                let tokenResult = cachedTokenResult;
                let tokenWasForceRefreshed = false;
                let isAdmin = !!cachedTokenResult.claims["admin"];

                logger.debug("Initial admin-claim check (cached token)", {
                    operation: "claims_check_cached",
                    userId: user.uid,
                    hasAdminClaim: isAdmin,
                });

                if (!isAdmin) {
                    logger.debug(
                        "No admin claim found; forcing token refresh",
                        {
                            operation: "claims_force_refresh",
                            userId: user.uid,
                        },
                    );
                    tokenResult = await user.getIdTokenResult(true);
                    tokenWasForceRefreshed = true;
                    isAdmin = !!tokenResult.claims["admin"];
                }

                if (cancelled) {
                    return;
                }

                setHasAdminClaim(isAdmin);
                setIsCheckingClaims(false);

                if (!isAdmin) {
                    logger.warn("Access denied: missing required admin claim", {
                        operation: "claims_access_denied",
                        userId: user.uid,
                        requiredClaims,
                        claimKeys: Object.keys(tokenResult.claims ?? {}),
                        tokenWasForceRefreshed,
                    });
                } else {
                    logger.debug("Access granted (admin claim present)", {
                        operation: "claims_access_granted",
                        userId: user.uid,
                    });
                }
            } catch (error) {
                logger.error(
                    "Error checking admin claims",
                    error instanceof Error ? error : undefined,
                    { operation: "claims_check_failed" },
                );
                if (!cancelled) {
                    setHasAdminClaim(false);
                    setIsCheckingClaims(false);
                }
            }
        };

        void checkAdminClaims();

        return () => {
            cancelled = true;
        };
    }, [adminE2eBypassEnabled, requiredClaims, requiresAdminClaim, user]);

    return {
        hasAdminClaim,
        isCheckingClaims,
        requiresAdminClaim,
    };
};

